import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useDebounce } from "@reactuses/core";
import { PageContainer } from "../components/core/basic/PageContainer";
import BreadcrumbDates, { type Breadcrumb } from "../components/core/blocks/BreadcrumbDates";
import MetricsCard from "../components/custom/merchandise/MetricsCard";
import { usePersistedTableState } from "../components/table/usePersistedTableState";
import { idsToVisibilityState, selectionStateToSelectedIds } from "../components/table/utils";
import { DatesProvider, useDates } from "../dates/DatesContext";
import useCategories from "../hooks/useCategories";
import { useAppSelector } from "../redux/hooks";
import { useModuleEnabled } from "../redux/selectors/modules";
import { useAcosOrRoas } from "../utils/acosOrRoas";
import { DASHBOARD_LINKS, AGENCY_DASHBOARD_BREADCRUMB_SPEC } from "../utils/dashboardLinks";
import getMetricSpec from "../modules/merchandising/utils/metrics";
import type { TSelector } from "../i18n/types";
import { useGetV4AdvReportByDate } from "../modules/perf_reports/api/ads/v4AdvReportByDate";
import { useGetV4AdvReportTotals } from "../modules/perf_reports/api/ads/v4AdvReportTotals";
import AdTypeSection from "../modules/advertising/components/adTypeSection/AdTypeSection";
import { useParams } from "@tanstack/react-router";
import { Accordion } from "../components/core/basic/Accordion";
import { AdReportTable, getAdTypeFilterOptions } from "../modules/perf_reports/ad_report/AdReportTable";
import useAdReportColumns from "../modules/perf_reports/ad_report/useAdReportColumns";
import AdPlanModal from "../components/core/blocks/AdPlan/AdPlanModal";
import { Button } from "../components/core/basic/Button";
import { HStack } from "../../styled-system/jsx";
import { AnnotationsTable } from "../components/custom/growth/AnnotationsTable";
import { useFetch } from "../hooks/api";
import { useStandardEntityMetrics } from "../modules/merchandising/components/tables/metricUtils";
import DashboardSelector from "../modules/advertising/components/DashboardSelector";
import { useGetV4CategoryReportTotals } from "../modules/perf_reports/api/category/v4CategoryReportTotals";
import { useGetV4CategoryReportByDate } from "../modules/perf_reports/api/category/v4CategoryReportByDate";
import QueryFunnel from "../modules/query_funnel/QueryFunnel";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../i18n/useDateFormatter";

interface Props {
  categoryId?: string;
  title: string;
}

export interface AdType {
  value: string | number;
  label: string | TSelector;
}

function AdvertisingReportBody({ categoryId, title }: Props) {
  const { t } = useTranslation(["advertising"]);
  const acosOrRoas = useAcosOrRoas();
  const [adType, setAdType] = useState<string | number | null>("all");
  const [adPlanOpen, setAdPlanOpen] = useState(false);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const { start, end, group } = useDates();
  const longDate = useDateFormatter("long");
  const startDate = start.format("YYYY-MM-DD");
  const endDate = end.format("YYYY-MM-DD");
  const [quickFilterValue, setQuickFilterValue] = useState("all");
  const mediaPlanMetrics = useStandardEntityMetrics("mediaPlan");
  const categoryMetrics = useStandardEntityMetrics("category");

  const columns = useAdReportColumns();
  const defaultSelectedColumns = [
    "state",
    "target_acos",
    "max_acos",
    "daily_budget",
    "created_datetime",
    "ad_sales",
    "ad_spend",
    "acos",
    "roas",
    "orders",
    "impressions",
    "clicks",
    "ctr",
    "conversion_rate",
    "cpc",
    "serving_hours",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id!),
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "adReport",
    initialState: {
      columnVisibility: {
        ...defaultHiddenCols,
        acos: acosOrRoas(true, false),
        roas: acosOrRoas(false, true),
      },
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        start: ["ad_name"],
        end: [],
      },
      sorting: [{ id: "ad_sales", desc: true }],
    },
  });
  const debounceSearch = useDebounce(state.globalFilter, 500);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);
  const categoryIdFilter = categoryId ? [{ id: "category_id", value: { op: "=", value: categoryId } }] : [];
  const categoryTotalsOptionsGetter = useGetV4CategoryReportTotals();
  const { data: categoryTotalsData } = useQuery({
    ...categoryTotalsOptionsGetter({ filters: categoryIdFilter }),
    placeholderData: keepPreviousData,
  });
  const categoryByDateOptionsGetter = useGetV4CategoryReportByDate();
  const { data: categoryByDateData } = useQuery({
    ...categoryByDateOptionsGetter({ filters: categoryIdFilter }),
    placeholderData: keepPreviousData,
  });

  const { categories } = useCategories(categoryId, "advertising/dashboard");
  const adTypeFilterOptions = getAdTypeFilterOptions(channel);

  const filters = useMemo(() => {
    const selectedIdsFilter =
      selectedIds.length > 0 ? [{ id: "ad_id", value: { op: "anyof", value: selectedIds } }] : [];
    const adTypeFilter = adType !== "all" ? [{ id: "ad_type", value: { op: "=", value: adType } }] : [];
    const categoryFilter = categoryId ? [{ id: "category_id", value: { op: "=", value: categoryId } }] : [];
    const quickFilter = quickFilterValue !== "all" ? [{ id: quickFilterValue, value: { op: "=", value: true } }] : [];
    return [...state.columnFilters, ...selectedIdsFilter, ...adTypeFilter, ...categoryFilter, ...quickFilter];
  }, [state.columnFilters, selectedIds, adType, categoryId, quickFilterValue]);

  const byDateOptionsGetter = useGetV4AdvReportByDate();
  const advByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters,
      search: debounceSearch,
    }),
  );
  const totalsOptionsGetter = useGetV4AdvReportTotals();
  const advTotalsReportQuery = useQuery(
    totalsOptionsGetter({
      filters,
      search: debounceSearch,
    }),
  );

  const showTotalSales = useMemo(() => {
    return !(
      debounceSearch ||
      selectedIds.length ||
      adType !== "all" ||
      state.columnFilters.length ||
      quickFilterValue !== "all"
    );
  }, [debounceSearch, selectedIds.length, adType, state.columnFilters.length, quickFilterValue]);

  const metrics = useMemo(() => {
    const data = {
      total: advTotalsReportQuery.data?.data,
    };
    // If we we're not filtering, searching, or selecting, we can include that category level data
    if (showTotalSales) {
      if (data.total && categoryTotalsData?.data) {
        data.total = {
          ...data.total,
          ...categoryTotalsData.data,
        };
      }
    } else {
      data.total = advTotalsReportQuery.data?.data;
    }
    const metrics = showTotalSales ? categoryMetrics : mediaPlanMetrics;
    return metrics.map((id) => getMetricSpec(id));
  }, [showTotalSales, mediaPlanMetrics, categoryMetrics, advTotalsReportQuery.data?.data, categoryTotalsData?.data]);

  const timeLineData = useMemo(() => {
    if (showTotalSales) {
      return categoryByDateData?.data;
    }
    return advByDateReportQuery.data?.data;
  }, [advByDateReportQuery.data?.data, categoryByDateData?.data, showTotalSales]);

  const { data: annotationData, isLoading: isAnnotationDataLoading } = useFetch<{ data: unknown[] }>(
    ["annotations", start, end, channel, categoryId, marketPlace.marketPlace],
    "/api/annotations/",
    {
      groupby: group,
      ts_after: `${startDate}T00:00:00`,
      ts_before: `${endDate}T23:59:59`,
      category: categoryId,
      marketplace: marketPlace.marketPlace,
      channel: channel === "amazon" ? "AMZ" : channel,
    },
    // ts_before and ts_after need values from categories to contain start and end date within query string
    {
      placeholderData: keepPreviousData,
      enabled: !!categoryId,
    },
  );

  const isStaff = useAppSelector((state) => state.user.is_staff);
  const debugModule = useModuleEnabled("DEBUG");
  const canViewDebug = isStaff && debugModule;
  return (
    <PageContainer withBreadcrumb>
      <MetricsCard
        id={showTotalSales ? "adversitingOverview" : "adversitingOverviewWithFilters"}
        title={title}
        totals={{
          data:
            showTotalSales && categoryTotalsData?.data ?
              { ...advTotalsReportQuery.data?.data, ...categoryTotalsData.data }
            : advTotalsReportQuery.data?.data,
          loading:
            (advTotalsReportQuery.isFetching &&
              (!advTotalsReportQuery.data || advTotalsReportQuery.isPlaceholderData)) ||
            (showTotalSales && !categoryTotalsData?.data),
        }}
        timeline={{
          data: timeLineData || [],
          loading:
            advByDateReportQuery.isFetching && (!advByDateReportQuery.data || advByDateReportQuery.isPlaceholderData),
        }}
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["ad_sales", "ad_spend", acosOrRoas("acos", "roas")]}
        group={group}
        showAnnotations={true}
        byline={
          <>
            {longDate.format(start.toDate())}
            {" - "}
            {longDate.format(end.toDate())}
          </>
        }
        context={"adReport"}
      >
        <HStack justifyContent={"end"}>
          <Button onPress={() => setAdPlanOpen(true)}>{t(($) => $.advertising["Create A New Media Plan"])}</Button>
          <AdPlanModal
            isOpen={adPlanOpen}
            onClose={() => setAdPlanOpen(false)}
            dashboard={false}
            categories={categories}
            categoryId={categoryId}
            // oxlint-disable-next-line @typescript-eslint/no-explicit-any
            {...({} as any)}
          />
        </HStack>
        <Accordion title={t(($) => $.advertising["Ad Types"])}>
          <AdTypeSection
            adTypeFilter={adTypeFilterOptions.find((o: AdType) => o.value === adType)}
            setAdTypeFilter={(adType: AdType) => {
              setAdType(adType.value);
            }}
            additionalFilters={filters}
            search={debounceSearch}
          />
        </Accordion>
        <Accordion title={t(($) => $.advertising["Media Plans"])} defaultExpanded>
          <AdReportTable
            state={state}
            initialState={initialState}
            setters={setters}
            quickFilterValue={quickFilterValue}
            setQuickFilterValue={setQuickFilterValue}
            adType={adType}
            setAdType={setAdType}
            additionalFilters={{
              categoryId,
              adType: String(adType),
            }}
            columns={columns}
          />
        </Accordion>
        {canViewDebug && <QueryFunnel category={categoryId!} />}

        <Accordion title={t(($) => $.advertising["Impact Analysis"])}>
          <AnnotationsTable
            data={annotationData?.data}
            isLoading={isAnnotationDataLoading}
            annotationType={"advertisement"}
          />
        </Accordion>
      </MetricsCard>
    </PageContainer>
  );
}
export default function AdvertisingDashboardV2() {
  const { t } = useTranslation(["advertising"]);

  const { id: rawCategoryId } = useParams({ from: "/_authenticated/user/advertising/dashboard/category/$id" });

  const categoryId = rawCategoryId === "all" ? undefined : rawCategoryId;

  const { currentCategoryDetails: categoryDetails, categories: categoryData } = useCategories(
    categoryId,
    "advertising/dashboard",
  );
  const categoryOptions = useMemo(
    () => [
      { href: "/user/advertising/dashboard/category/all", name: t(($) => $.advertising["All Categories"]) },
      ...(categoryData ?? []),
    ],
    [categoryData, t],
  );
  const currentCategory = useMemo(() => {
    if (categoryId) {
      return {
        name: categoryDetails.leaf_category_name ?? "",
        href: `/user/advertising/dashboard/category/${categoryId}`,
      };
    }
    return {
      name: t(($) => $.advertising["All Categories"]),
      href: "/user/advertising/dashboard/category/all",
    };
  }, [categoryId, categoryDetails.leaf_category_name, t]);

  const breadCrumbItems: Breadcrumb[] = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    {
      name: t(($) => $.advertising["Advertisement Dashboard"]),
      href: "/user/advertising/dashboard",
      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      dropdownSubtitle: t(($) => $.advertising["Reports"]),
    },
  ];

  breadCrumbItems.push({
    name: currentCategory.name,
    href: currentCategory.href,
    dropdownLinkOptions: categoryOptions,
    searchable: true,
    searchPlaceholderText: t(($) => $.advertising["Search categories..."]),
    dropdownSubtitle: t(($) => $.advertising["Categories"]),
  });

  return (
    <DatesProvider>
      <DashboardSelector />
      <BreadcrumbDates items={breadCrumbItems} />
      <AdvertisingReportBody
        categoryId={categoryId}
        title={categoryId ? currentCategory.name : t(($) => $.advertising["Advertising Overview"])}
      />
    </DatesProvider>
  );
}
